import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import AdminBackLink from '../components/AdminBackLink.jsx';
import FullIconSelect from '../components/FullIconSelect.jsx';
import './ModuleAdmin.css';

const emptyFeeInclude = {
  name: '',
  icon: '',
  description: '',
  order: 0
};

const TrekFeeIncludeForm = () => {
  const { trekId, feeId } = useParams();
  const isEdit = feeId && feeId !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: trekRes } = useQuery(
    ['trek', trekId],
    () => trekAPI.get(trekId),
    { enabled: !!trekId }
  );
  const trek = trekRes?.data?.data;

  const { data: feeRes } = useQuery(
    ['trek-fees', trekId],
    () => trekAPI.listFeesIncludes(trekId),
    { enabled: isEdit && !!trekId }
  );
  const existingFee = isEdit
    ? (feeRes?.data?.data || []).find((f) => String(f.id) === String(feeId))
    : null;

  const [form, setForm] = useState(emptyFeeInclude);
  const didExplicitSubmitRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);


  useEffect(() => {
    if (existingFee) {
      setForm({
        name: existingFee.name || '',
        icon: existingFee.icon || '',
        description: existingFee.description || '',
        order: existingFee.order != null ? existingFee.order : 0
      });
    }
  }, [existingFee]);

  const mutation = useMutation(
    (payload) =>
      isEdit
        ? trekAPI.updateFeeInclude(trekId, feeId, payload)
        : trekAPI.createFeeInclude(trekId, payload),
    {
      onSuccess: (response) => {
        if (!isMountedRef.current) return;
        if (!didExplicitSubmitRef.current) return;
        queryClient.invalidateQueries(['trek-fees', trekId]);
        // Stay on form after save to avoid intermittent auto-close cycles.
        if (!isEdit) {
          const createdId = response?.data?.data?.id;
          if (createdId) navigate(`/treks/${trekId}/fees-includes/${createdId}`, { replace: true });
        }
        showToast({
          message: isEdit ? 'Fees includes updated successfully.' : 'Fees includes item created successfully.',
          severity: 'success'
        });
        didExplicitSubmitRef.current = false;
      }
    }
  );

  const buildPayload = () => ({
    name: form.name.trim(),
    icon: form.icon.trim() || null,
    description: form.description.trim() || null,
    order: form.order ? Number(form.order) : 0
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast({
        message: 'Name is required.',
        severity: 'warning'
      });
      return;
    }
    const payload = buildPayload();
    didExplicitSubmitRef.current = true;
    mutation.mutate(payload);
  };

  return (
    <div className="module-page">
      <AdminBackLink
        label="Back to treks"
        onNavigate={() => {
          didExplicitSubmitRef.current = true;
          navigate('/treks', { state: { openTrekId: trekId } });
        }}
      />
      <div className="module-header">
        <div>
          <h1 className="module-title">{isEdit ? 'Edit Inclusion' : 'Add Inclusion'}</h1>
          <p className="module-subtitle">
            {trek ? trek.name : 'Add fees included details for trek'}
          </p>
        </div>
      </div>

      <div className="module-card">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Icon (price card on event page)</label>
              <FullIconSelect
                value={form.icon || ''}
                onChange={(icon) => setForm((p) => ({ ...p, icon }))}
                defaultLabel="Default (briefcase)"
              />
              <p className="module-hint" style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: '#64748b' }}>
                Appears on the price card and Inclusion &amp; Exclusion section on the trek page.
              </p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea
                rows="4"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <label>Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
              />
            </div>
          </div>

          <div className="legacy-main-actions">
            <button className="module-btn" type="button" disabled={mutation.isLoading} onClick={handleSubmit}>
              {isEdit ? 'Update Fees Includes' : 'Save Fees Includes'}
            </button>
            <button
              className="module-btn secondary"
              type="button"
              onClick={() => {
                didExplicitSubmitRef.current = true;
                navigate('/treks', { state: { openTrekId: trekId } });
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrekFeeIncludeForm;

